// File: C:\Users\s-naidu\Desktop\merchant-card\frontend\src\merchant\pages\forms\Form5SalesMethod.tsx

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import MerchantLayout from "../../components/MerchantLayout";
import { apiGet, apiPut } from "../../api";

type Form5 = {
  // --- Main (美容医療/エステ/異業種/その他) ---
  marketing_method: string; // 集客方法（選択）
  customer_segment: string; // 顧客層（テキスト）

  male_percent: string; // 男性○%
  female_percent: string; // 女性○%

  customer_ratio_employee: string; // 会社員○%
  customer_ratio_housewife: string; // 主婦○%
  customer_ratio_student: string; // 学生○%

  age_min: string; // ○歳
  age_max: string; // ○歳

  cancellation_count: string; // 中途解約件数（数字）

  // ✅ 必須（美容医療のみ適用）
  medical_liability_insurance: string; // 有/無

  // --- BtoB ---
  b2b_marketing_method: string; // 集客方法（選択）
  b2b_customer_segment: string; // 顧客層（テキスト）

  b2b_customer_ratio_corporate: string; // 法人○%
  b2b_customer_ratio_sole: string; // 個人事業主○%

  is_completed: boolean;
};

const empty: Form5 = {
  marketing_method: "",
  customer_segment: "",

  male_percent: "",
  female_percent: "",

  customer_ratio_employee: "",
  customer_ratio_housewife: "",
  customer_ratio_student: "",

  age_min: "",
  age_max: "",

  cancellation_count: "",

  medical_liability_insurance: "",

  b2b_marketing_method: "",
  b2b_customer_segment: "",

  b2b_customer_ratio_corporate: "",
  b2b_customer_ratio_sole: "",

  is_completed: false,
};

// Professional, common marketing acquisition choices.
const MARKETING_OPTIONS = [
  "紹介（既存顧客）",
  "Web広告（Google/SNS）",
  "SNS（Instagram/X/TikTok）",
  "口コミサイト",
  "チラシ・紙媒体",
  "看板・通行",
  "イベント・展示会",
  "電話営業",
  "その他",
];


const splitMarketing = (value: string): string[] => {
  if (!value) return [];
  return value
    .split(",")
    .map((v) => v.trim())
    .filter(Boolean);
};

const joinMarketing = (values: string[]): string => {
  return values.join(",");
};

type IndustryKey = "美容医療" | "エステティック" | "異業種" | "その他" | "BtoB" | "";

export default function Form5SalesMethod() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [data, setData] = useState<Form5>(empty);

  const [marketingList, setMarketingList] = useState<string[]>([]);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // ✅ 業種（admin selected）を /merchant/me から取得して表示制御する
  const [industry, setIndustry] = useState<IndustryKey>("");

  const isBtoB = industry === "BtoB";
  const isBeautyMedical = industry === "美容医療";

  // ✅ Completion rule (BUSINESS LOGIC: DO NOT CHANGE)
  // - BtoB: require 4 BtoB fields
  // - Non-BtoB: require all main fields
  // - Beauty medical: plus insurance required
  const canComplete = useMemo(() => {
    // BtoB completion
    if (isBtoB) {
      return (
        data.b2b_marketing_method.trim() !== "" &&
        data.b2b_customer_segment.trim() !== "" &&
        data.b2b_customer_ratio_corporate.trim() !== "" &&
        data.b2b_customer_ratio_sole.trim() !== ""
      );
    }

        // 美容医療: required item only
    if (isBeautyMedical) {
      return data.medical_liability_insurance.trim() !== "";
    }

    // Main completion (エステ / 異業種 / その他)
    const mainOk =
      data.marketing_method.trim() !== "" &&
      data.male_percent.trim() !== "" &&
      data.female_percent.trim() !== "" &&
      data.customer_ratio_employee.trim() !== "" &&
      data.customer_ratio_housewife.trim() !== "" &&
      data.customer_ratio_student.trim() !== "" &&
      data.age_min.trim() !== "" &&
      data.age_max.trim() !== "" &&
      data.cancellation_count.trim() !== "";

    if (!mainOk) return false;

    return true;
  }, [
    isBtoB,
    isBeautyMedical,
    data.b2b_marketing_method,
    data.b2b_customer_segment,
    data.b2b_customer_ratio_corporate,
    data.b2b_customer_ratio_sole,
    data.marketing_method,
    data.customer_segment,
    data.male_percent,
    data.female_percent,
    data.customer_ratio_employee,
    data.customer_ratio_housewife,
    data.customer_ratio_student,
    data.age_min,
    data.age_max,
    data.cancellation_count,
    data.medical_liability_insurance,
  ]);

  // ✅ Step③ UI (no business logic change)
  const statusLabel = canComplete ? "入力済み" : "未完了";
  

  useEffect(() => {
    const run = async () => {
      setError("");
      setSuccess("");

      try {
        // 1) 業種取得（BtoB判定のため industry を使う）
        try {
          const me = await apiGet("/merchant/me");
          const m = me?.merchant;
          const ind = (m?.industry || "").trim();
          setIndustry(ind as IndustryKey);
        } catch {
          // ignore (page still usable)
          setIndustry("");
        }

        // 2) フォームデータ取得
        const json = await apiGet("/merchant/forms/sales-method");
        const d = json?.data;

        if (d) {
          setData({
            marketing_method: d.marketing_method || "",
            customer_segment: d.customer_segment || "",

            male_percent: d.male_percent != null ? String(d.male_percent) : "",
            female_percent: d.female_percent != null ? String(d.female_percent) : "",

            customer_ratio_employee:
              d.customer_ratio_employee != null ? String(d.customer_ratio_employee) : "",
            customer_ratio_housewife:
              d.customer_ratio_housewife != null ? String(d.customer_ratio_housewife) : "",
            customer_ratio_student:
              d.customer_ratio_student != null ? String(d.customer_ratio_student) : "",

            age_min: d.age_min != null ? String(d.age_min) : "",
            age_max: d.age_max != null ? String(d.age_max) : "",

            cancellation_count: d.cancellation_count != null ? String(d.cancellation_count) : "",

            medical_liability_insurance: d.medical_liability_insurance || "",

            b2b_marketing_method: d.b2b_marketing_method || "",
            b2b_customer_segment: d.b2b_customer_segment || "",

            b2b_customer_ratio_corporate:
              d.b2b_customer_ratio_corporate != null ? String(d.b2b_customer_ratio_corporate) : "",
            b2b_customer_ratio_sole:
              d.b2b_customer_ratio_sole != null ? String(d.b2b_customer_ratio_sole) : "",

            is_completed: Boolean(d.is_completed),

            
          });
           setMarketingList(splitMarketing(d.marketing_method || ""));
        } else {
          setData(empty);
          setMarketingList([]);
        }

        setLoading(false);
      } catch (e: any) {
        setError(
          e?.message ||
            "販売方法の取得に失敗しました。バックエンドが起動しているか、Form5のAPIが実装済みか確認してください。"
        );
        setLoading(false);
      }
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

    const onSave = async (): Promise<boolean> => {
    setError("");
    setSuccess("");
    setSaving(true);

    try {
      const json = await apiPut("/merchant/forms/sales-method", {
        marketing_method: joinMarketing(marketingList),
        customer_segment: data.customer_segment,

        male_percent: data.male_percent,
        female_percent: data.female_percent,

        customer_ratio_employee: data.customer_ratio_employee,
        customer_ratio_housewife: data.customer_ratio_housewife,
        customer_ratio_student: data.customer_ratio_student,

        age_min: data.age_min,
        age_max: data.age_max,

        cancellation_count: data.cancellation_count,

        medical_liability_insurance: isBeautyMedical ? data.medical_liability_insurance : "",

        b2b_marketing_method: data.b2b_marketing_method,
        b2b_customer_segment: data.b2b_customer_segment,
        b2b_customer_ratio_corporate: data.b2b_customer_ratio_corporate,
        b2b_customer_ratio_sole: data.b2b_customer_ratio_sole,

        is_completed: canComplete,
      });

      setData((prev) => ({ ...prev, is_completed: canComplete }));
      const okCompleted = Boolean(json?.is_completed ?? canComplete);
      setSuccess(okCompleted ? "保存しました（入力済み）" : "保存しました（未完了）");
      setSaving(false);
      return true;
    } catch (e: any) {
      setError(
        e?.message ||
          "保存に失敗しました。バックエンドが起動しているか、Form5のAPIが実装済みか確認してください。"
      );
      setSaving(false);
      return false;
    }
  };

    const onBack = async () => {
    const ok = await onSave();
    if (!ok) return;
    navigate("/merchant/forms/products");
  };

  const onNext = async () => {
    const ok = await onSave();
    if (!ok) return;
    navigate("/merchant/forms/store-list");
  };


  const onList = async () => {
  const ok = await onSave();
  if (!ok) return;
  navigate("/merchant/register");
};











  return (
    <MerchantLayout title="5. 販売方法" active="forms">
      {loading && <div style={{ color: "#475569" }}>読み込み中...</div>}

      {error && (
        <div
          style={{
            marginBottom: 12,
            background: "#fff1f2",
            border: "1px solid #fecdd3",
            color: "#b91c1c",
            padding: "10px 12px",
            borderRadius: 12,
            fontWeight: 800,
          }}
        >
          {error}
        </div>
      )}

      {success && (
        <div
          style={{
            marginBottom: 12,
            background: "#ecfdf5",
            border: "1px solid #bbf7d0",
            color: "#166534",
            padding: "10px 12px",
            borderRadius: 12,
            fontWeight: 900,
          }}
        >
          {success}（いつでも編集できます）
        </div>
      )}

      {!loading && (
        <>
          {/* ✅ Current industry badge */}
          <div style={{ marginBottom: 12, display: "flex", gap: 10, alignItems: "center" }}>
            <div style={{ fontWeight: 900, color: "#0f172a" }}>業種：</div>
            <div
              style={{
                background: "#0b1f3b",
                color: "#fff",
                fontWeight: 900,
                padding: "6px 12px",
                borderRadius: 999,
                fontSize: 12,
              }}
            >
              {(industry || "未設定").trim() || "未設定"}
            </div>
          </div>

          {/* =========================
              Main section (美容医療/エステ/異業種/その他)
              - BtoBの場合は非表示
             ========================= */}
          {!isBtoB && (
            <div
              style={{
                border: "1px solid #cbd5e1",
                borderRadius: 12,
                overflow: "hidden",
                background: "#fff",
              }}
            >
              <div
                style={{
                  padding: "10px 12px",
                  fontWeight: 900,
                  background: "#f8fafc",
                  borderBottom: "1px solid #e2e8f0",
                }}
              >
                販売方法
              </div>

              <div style={{ padding: 12 }}>
                {/* Industry applicability (display only) */}
                

                <div
  style={{
    display: "grid",
    gridTemplateColumns: "minmax(0,1fr)",
    gap: 20,
    alignItems: "start",
  }}
>
                                    <Field label="集客方法">
                    <div
                      style={{
                        border: "1px solid #cbd5e1",
                        borderRadius: 12,
                        padding: 12,
                        background: "#fff",
                      }}
                    >
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
                          gap: 10,
                        }}
                      >
                        {MARKETING_OPTIONS.map((option) => {
                          const checked = marketingList.includes(option);

                          return (
                            <label
                              key={option}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 10,
                                border: "1px solid #e2e8f0",
                                borderRadius: 10,
                                padding: "10px 12px",
                                background: checked ? "#eff6ff" : "#ffffff",
                                cursor: "pointer",
                                fontWeight: 400,
                                color: "#0f172a",
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={(e) => {
                                  const nextList = e.target.checked
                                    ? [...marketingList, option]
                                    : marketingList.filter((item) => item !== option);

                                  setMarketingList(nextList);
                                  setData({
                                    ...data,
                                    marketing_method: joinMarketing(nextList),
                                  });
                                }}
                              />
                              <span>{option}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  </Field>

                 
                </div>

                <div style={{ height: 12 }} />

                {/* 男女比 */}
                <RowBlock
                  title="男女比"
                  industryMarks={["○", "○", "○", "○"]}
                  left={
                    <Field label="男性">
  <SuffixInput
    value={data.male_percent}
    onChange={(value) => setData({ ...data, male_percent: value })}
    placeholder="例）40"
    suffix="%"
  />
</Field>
                  }
                  mid={
                    <Field label="女性">
  <SuffixInput
    value={data.female_percent}
    onChange={(value) => setData({ ...data, female_percent: value })}
    placeholder="例）60"
    suffix="%"
  />
</Field>
                  }
                  right={<div />}
                />

                <div style={{ height: 10 }} />

                {/* 顧客構成比 */}
                <RowBlock
                  title="顧客構成比"
                  industryMarks={["○", "○", "○", "○"]}
                  left={
                    <Field label="会社員">
  <SuffixInput
    value={data.customer_ratio_employee}
    onChange={(value) =>
      setData({ ...data, customer_ratio_employee: value })
    }
    placeholder="例）50"
    suffix="%"
  />
</Field>
                  }
                  mid={
                    <Field label="主婦">
  <SuffixInput
    value={data.customer_ratio_housewife}
    onChange={(value) =>
      setData({ ...data, customer_ratio_housewife: value })
    }
    placeholder="例）30"
    suffix="%"
  />
</Field>
                  }
                  right={
                    <Field label="学生">
  <SuffixInput
    value={data.customer_ratio_student}
    onChange={(value) =>
      setData({ ...data, customer_ratio_student: value })
    }
    placeholder="例）20"
    suffix="%"
  />
</Field>
                  }
                />

                <div style={{ height: 10 }} />

                {/* 年齢層 */}
                <RowBlock
                  title="年齢層"
                  industryMarks={["○", "○", "○", "○"]}
                  left={
                    <Field label="最小">
  <SuffixInput
    value={data.age_min}
    onChange={(value) => setData({ ...data, age_min: value })}
    placeholder="例）20"
    suffix="歳"
  />
</Field>
                  }
                  mid={
                    <Field label="最大">
  <SuffixInput
    value={data.age_max}
    onChange={(value) => setData({ ...data, age_max: value })}
    placeholder="例）45"
    suffix="歳"
  />
</Field>
                  }
                  right={<div />}
                />

                <div style={{ height: 10 }} />

                {/* 中途解約件数 */}
                <RowBlock
                  title="中途解約件数（月間平均）"
                  industryMarks={["○", "○", "○", "○"]}
                  left={
                    <Field label="件数">
  <SuffixInput
    value={data.cancellation_count}
    onChange={(value) =>
      setData({ ...data, cancellation_count: value })
    }
    placeholder="例）3"
    suffix="件"
  />
</Field>
                  }
                  mid={<div />}
                  right={<div />}
                />

                <div style={{ height: 10 }} />

                {/* ✅ 美容医療のみ表示（必須） */}
                {isBeautyMedical && (
                  <RowBlock
                    title="美容医療賠償責任制度の加入の有無"
                    industryMarks={["○", "×", "×", "×"]}
                    left={
                      <Field label="加入の有無" required>
                        <select
                          style={inputStyle}
                          value={data.medical_liability_insurance}
                          onChange={(e) =>
                            setData({ ...data, medical_liability_insurance: e.target.value })
                          }
                        >
                          <option value="">選択してください</option>
                          {["有", "無"].map((o) => (
                            <option key={o} value={o}>
                              {o}
                            </option>
                          ))}
                        </select>
                      </Field>
                    }
                    mid={<div />}
                    right={<div />}
                  />
                )}
              </div>
            </div>
          )}

          {/* =========================
              BtoB section
              - BtoBの場合のみ表示
             ========================= */}
          {isBtoB && (
            <div
              style={{
                marginTop: 0,
                border: "1px solid #cbd5e1",
                borderRadius: 12,
                overflow: "hidden",
                background: "#fff",
              }}
            >
              <div
                style={{
                  padding: "10px 12px",
                  fontWeight: 900,
                  background: "#f8fafc",
                  borderBottom: "1px solid #e2e8f0",
                }}
              >
                BtoB
              </div>

              <div style={{ padding: 12 }}>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)",
                    gap: 20,
                    alignItems: "start",
                  }}
                >
                  <Field label="集客方法">
                    <select
                      style={inputStyle}
                      value={data.b2b_marketing_method}
                      onChange={(e) =>
                        setData({ ...data, b2b_marketing_method: e.target.value })
                      }
                    >
                      <option value="">選択してください</option>
                      {MARKETING_OPTIONS.map((o) => (
                        <option key={o} value={o}>
                          {o}
                        </option>
                      ))}
                    </select>
                  </Field>

                  
                </div>

                <div style={{ height: 12 }} />

                <RowBlock
                  title="顧客構成比"
                  industryMarks={["○", "○", "○", "○"]}
                  left={
                    <Field label="法人（％）">
                      <input
                        style={inputStyle}
                        value={data.b2b_customer_ratio_corporate}
                        onChange={(e) =>
                          setData({ ...data, b2b_customer_ratio_corporate: e.target.value })
                        }
                        placeholder="例）70"
                      />
                    </Field>
                  }
                  mid={
                    <Field label="個人事業主（％）">
                      <input
                        style={inputStyle}
                        value={data.b2b_customer_ratio_sole}
                        onChange={(e) =>
                          setData({ ...data, b2b_customer_ratio_sole: e.target.value })
                        }
                        placeholder="例）30"
                      />
                    </Field>
                  }
                  right={<InfoBox text="備考：法人○％、個人事業主○％" />}
                />
              </div>
            </div>
          )}

                    <div
            style={{
              marginTop: 18,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 12,
            }}
          >
            <button
              onClick={onBack}
              disabled={saving}
              style={{
                padding: "12px 16px",
                borderRadius: 12,
                border: "none",
                background: "#dc2626",
                color: "#fff",
                fontWeight: 900,
                cursor: saving ? "not-allowed" : "pointer",
                opacity: saving ? 0.7 : 1,
                transition: "background 0.2s ease",
              }}
            >
              {saving ? "保存中..." : "戻る"}
            </button>

            <button
              onClick={onSave}
              disabled={saving}
              style={{
                padding: "12px 16px",
                borderRadius: 12,
                border: "none",
                background: "#1d4ed8",
                color: "#fff",
                fontWeight: 900,
                cursor: saving ? "not-allowed" : "pointer",
                opacity: saving ? 0.7 : 1,
                transition: "background 0.2s ease",
              }}
            >
              {saving ? "保存中..." : "保存"}
            </button>

            <button
  onClick={onList}
  disabled={saving}
  style={{
    padding: "12px 16px",
    borderRadius: 12,
    border: "none",
    background: "#64748b",
    color: "#fff",
    fontWeight: 900,
    cursor: saving ? "not-allowed" : "pointer",
    opacity: saving ? 0.7 : 1,
    transition: "background 0.2s ease",
  }}
>
  一覧
</button>

            <button
              onClick={onNext}
              disabled={saving}
              style={{
                padding: "12px 16px",
                borderRadius: 12,
                border: "none",
                background: "#dc2626",
                color: "#fff",
                fontWeight: 900,
                cursor: saving ? "not-allowed" : "pointer",
                opacity: saving ? 0.7 : 1,
                transition: "background 0.2s ease",
              }}
            >
              {saving ? "保存中..." : "次へ"}
            </button>
          </div>

          <div style={{ marginTop: 10, fontSize: 13, color: "#475569" }}>
            ステータス：{" "}
            <span style={{ fontWeight: 900, color: canComplete ? "#dc2626" : "#64748b" }}>
              {statusLabel}
            </span>
          </div>

          {!canComplete && (
            <div style={{ fontSize: 12, color: "#475569", fontWeight: 800 }}>
              ※ 必須項目が未入力のため「未完了」になります
            </div>
          )}
        </>
      )}
    </MerchantLayout>
  );
}

/** ✅ Required mark style like Form1/2 */
function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 13, fontWeight: 900, marginBottom: 6, color: "#0f172a" }}>
        {label} {required ? <span style={{ color: "#b91c1c" }}>（必須）</span> : null}
      </div>
      {children}
    </div>
  );
}

/** ✅ input styles same as Form1/2 */
const inputStyle: React.CSSProperties = {
  width: "100%",
  height: 44,
  padding: "0 12px",
  borderRadius: 10,
  border: "1px solid #cbd5e1",
  background: "#fff",
  color: "#0f172a",
  fontSize: 14,
  outline: "none",
  boxSizing: "border-box",
};



function SuffixInput({
  value,
  onChange,
  placeholder,
  suffix,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  suffix: string;
}) {
  return (
    <div style={{ position: "relative", width: "100%" }}>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        style={{
          ...inputStyle,
          paddingRight: 70,
        }}
      />
      <span
        style={{
          position: "absolute",
          right: 12,
          top: "50%",
          transform: "translateY(-50%)",
          color: "#475569",
          fontSize: 14,
          fontWeight: 700,
          pointerEvents: "none",
          whiteSpace: "nowrap",
        }}
      >
        {suffix}
      </span>
    </div>
  );
}

function Pill({ text, active }: { text: string; active?: boolean }) {
  return (
    <div
      style={{
        background: active ? "#1d4ed8" : "#eef2ff",
        border: `1px solid ${active ? "#1d4ed8" : "#c7d2fe"}`,
        color: active ? "#ffffff" : "#1e3a8a",
        fontWeight: 900,
        fontSize: 12,
        padding: "6px 10px",
        borderRadius: 999,
        textAlign: "center",
      }}
    >
      {text}
    </div>
  );
}

function Mark({ v }: { v: string }) {
  const isOk = v === "○";
  return (
    <div
      style={{
        textAlign: "center",
        fontWeight: 1000,
        color: isOk ? "#166534" : "#b91c1c",
        background: isOk ? "#ecfdf5" : "#fff1f2",
        border: `1px solid ${isOk ? "#bbf7d0" : "#fecdd3"}`,
        padding: "6px 0",
        borderRadius: 10,
      }}
    >
      {v}
    </div>
  );
}

function InfoBox({ text }: { text: string }) {
  return (
    <div
      style={{
        minHeight: 44,
        display: "flex",
        alignItems: "center",
        padding: "0 12px",
        borderRadius: 10,
        border: "1px dashed #cbd5e1",
        color: "#475569",
        background: "#f8fafc",
        fontWeight: 800,
        fontSize: 13,
      }}
    >
      {text}
    </div>
  );
}

function RowBlock({
  title,
  industryMarks,
  left,
  mid,
  right,
}: {
  title: string;
  industryMarks: [string, string, string, string];
  left: React.ReactNode;
  mid: React.ReactNode;
  right: React.ReactNode;
}) {
  return (
    <div
      style={{
        border: "1px solid #e2e8f0",
        borderRadius: 12,
        padding: 12,
        background: "#ffffff",
      }}
    >
      <div style={{ fontWeight: 1000, marginBottom: 10, color: "#0f172a" }}>{title}</div>

      

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr) minmax(0,1fr)",
          gap: 20,
          alignItems: "start",
        }}
      >
        {left}
        {mid}
        {right}
      </div>
    </div>
  );
}
